import React, { useState, useMemo } from 'react';
import CalcShell, { StepsPanel } from '../CalcShell';
import { CalcNetPanel, Row, UnitInput, FeetInches, RadioGroup, ResultTable } from '../_fitnessHelpers';

/* Shared inputs used by Calorie / Macro / TDEE / Carb / Fat / Protein */
export function useCaloricInputs(defaults = {}) {
  const [tab, setTab] = useState(0);
  const [age, setAge] = useState(defaults.age ?? 25);
  const [gender, setGender] = useState(defaults.gender ?? 'male');
  const [ft, setFt] = useState(defaults.ft ?? 5);
  const [inches, setInches] = useState(defaults.inches ?? 10);
  const [lb, setLb] = useState(defaults.lb ?? 165);
  const [cm, setCm] = useState(defaults.cm ?? 178);
  const [kg, setKg] = useState(defaults.kg ?? 75);
  const [activity, setActivity] = useState(defaults.activity ?? 1.465);
  const [formula, setFormula] = useState('mifflin');
  const [bodyFat, setBodyFat] = useState(20);
  const [foodEnergy, setFoodEnergy] = useState('kcal');

  const clear = () => {
    setAge(25); setGender('male'); setFt(5); setInches(10); setLb(165);
    setCm(178); setKg(75); setActivity(1.465); setFormula('mifflin'); setBodyFat(20);
  };

  const heightCm = tab === 0 ? ((+ft || 0) * 12 + (+inches || 0)) * 2.54 : +cm || 0;
  const weightKg = tab === 0 ? (+lb || 0) * 0.453592 : +kg || 0;
  let bmr = 0;
  if (formula === 'mifflin') bmr = 10*weightKg + 6.25*heightCm - 5*age + (gender==='male'?5:-161);
  else if (formula === 'harris') bmr = gender==='male' ? 88.362+13.397*weightKg+4.799*heightCm-5.677*age : 447.593+9.247*weightKg+3.098*heightCm-4.330*age;
  else bmr = 370 + 21.6 * (weightKg * (1 - (bodyFat/100)));
  const tdee = bmr * activity;

  return {
    tab, setTab, age, setAge, gender, setGender,
    ft, setFt, inches, setInches, lb, setLb, cm, setCm, kg, setKg,
    activity, setActivity, formula, setFormula, bodyFat, setBodyFat, foodEnergy, setFoodEnergy,
    heightCm, weightKg, bmr, tdee, clear,
  };
}

const ACTIVITY_OPTIONS = [
  { value: 1.2, label: 'Sedentary: little or no exercise' },
  { value: 1.375, label: 'Light: exercise 1-3 times/week' },
  { value: 1.465, label: 'Moderate: exercise 4-5 times/week' },
  { value: 1.55, label: 'Active: daily exercise or intense exercise 3-4 times/week' },
  { value: 1.725, label: 'Very Active: intense exercise 6-7 times/week' },
  { value: 1.9, label: 'Extra Active: very intense exercise daily, or physical job' },
];

export default function CalorieCalc() {
  const s = useCaloricInputs();
  const heightInputs = s.tab === 0
    ? <div className="grid grid-cols-2 gap-2 flex-1"><FeetInches ft={s.ft} inches={s.inches} onFt={s.setFt} onIn={s.setInches} testidPrefix="cal-h" /></div>
    : <UnitInput value={s.cm} onChange={s.setCm} unit="cm" testid="cal-h-cm" />;
  const weightInput = s.tab === 0
    ? <UnitInput value={s.lb} onChange={s.setLb} unit="pounds" testid="cal-w-lb" />
    : <UnitInput value={s.kg} onChange={s.setKg} unit="kg" testid="cal-w-kg" />;

  const targets = {
    maintain: s.tdee,
    mildLoss: s.tdee - 250,
    loss: s.tdee - 500,
    extLoss: s.tdee - 1000,
    mildGain: s.tdee + 250,
    gain: s.tdee + 500,
  };

  return (
    <CalcShell look="cnet" title="Calorie Calculator" subtitle="Estimate the number of calories you need each day based on age, sex, weight, height, and activity.">
      <CalcNetPanel
        slug="cal"
        activeTab={s.tab}
        onTabChange={s.setTab}
        onClear={s.clear}
        settings={
          <>
            <Row label="BMR Formula">
              <select value={s.formula} onChange={e => s.setFormula(e.target.value)} data-testid="cal-formula"
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm">
                <option value="mifflin">Mifflin-St Jeor (recommended)</option>
                <option value="harris">Revised Harris-Benedict</option>
                <option value="katch">Katch-McArdle</option>
              </select>
            </Row>
            {s.formula === 'katch' && <Row label="Body Fat"><UnitInput value={s.bodyFat} onChange={s.setBodyFat} unit="%" testid="cal-bf" /></Row>}
            <Row label="Result Unit">
              <select value={s.foodEnergy} onChange={e => s.setFoodEnergy(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="cal-unit">
                <option value="kcal">Calorie (kcal)</option>
                <option value="kj">Kilojoule (kJ)</option>
              </select>
            </Row>
          </>
        }
        right={
          <div>
            <div className="text-lg font-semibold text-slate-800 mb-1">Based on the Mifflin-St Jeor Equation</div>
            <div className="text-xl md:text-2xl font-bold text-[#2f9c3f]" data-testid="cal-tdee">
              {Math.round(s.tdee).toLocaleString()} Calories/day
            </div>
            <p className="text-sm text-slate-700 mt-3">to <b>maintain</b> weight</p>
            <ResultTable
              testid="cal-goal-table"
              headers={['Goal', 'Calories/day']}
              rows={[
                ['Maintain weight', <span className="font-bold text-[#2f9c3f]">{Math.round(targets.maintain).toLocaleString()}</span>],
                ['Mild weight loss  0.25 kg/week (~0.5 lb)', Math.round(targets.mildLoss).toLocaleString()],
                ['Weight loss  0.5 kg/week (~1 lb)', <span className="font-bold text-[#2f9c3f]">{Math.round(targets.loss).toLocaleString()}</span>],
                ['Extreme weight loss  1 kg/week (~2 lb)', Math.round(targets.extLoss).toLocaleString()],
                ['Mild weight gain  0.25 kg/week', Math.round(targets.mildGain).toLocaleString()],
                ['Weight gain  0.5 kg/week', Math.round(targets.gain).toLocaleString()],
              ]}
            />
            <p className="mt-3 text-xs text-slate-600">Zig-zag method: vary daily intake ±10-20% around the target to break plateaus while keeping the weekly average constant.</p>
          </div>
        }
      >
        <Row label="Age"><UnitInput value={s.age} onChange={s.setAge} testid="cal-age" /><span className="text-slate-500 text-xs whitespace-nowrap">ages 15 – 80</span></Row>
        <Row label="Gender"><RadioGroup name="cal-g" value={s.gender} onChange={s.setGender} options={[{value:'male',label:'male'},{value:'female',label:'female'}]} /></Row>
        <Row label="Height">{heightInputs}</Row>
        <Row label="Weight">{weightInput}</Row>
        <Row label="Activity">
          <select value={s.activity} onChange={e => s.setActivity(+e.target.value)}
            className="flex-1 px-3 py-2 bg-white border border-slate-400 rounded-sm text-sm" data-testid="cal-activity">
            {ACTIVITY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </Row>
      </CalcNetPanel>

      <StepsPanel steps={[
        `Compute BMR (Mifflin-St Jeor): 10·W + 6.25·H − 5·A + (${s.gender==='male'?'+5':'−161'})`,
        `BMR ≈ ${Math.round(s.bmr).toLocaleString()} cal/day; TDEE = BMR × activity(${s.activity}) = <b>${Math.round(s.tdee).toLocaleString()}</b>`,
        `To lose ~0.5 kg/week create a 500 kcal daily deficit → target ≈ ${Math.round(targets.loss).toLocaleString()} cal/day`,
      ]} />
    </CalcShell>
  );
}
